import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Home, Trophy } from "lucide-react";
import { projects as allProjects } from "../data/projects";

const HERO_IMAGE = "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/4o74fjm8_IMG_2502%20-%20Copy.webp";
const NH_LOGO = "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/dgy84abe_NH%20INTERIOR%20DESIGN%20LOGO.png";
const COVER_BG = 'linear-gradient(135deg, #F5F3EF, #FAF9F6)';

function HoverPreviewImage({ project }) {
  if (!project.images?.[0]) return null;
  return (
    <img
      src={project.images[0]}
      alt={project.title}
      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
    />
  );
}

function ProjectCoverImage({ project }) {
  if (project.comingSoon) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-warmBrown/20 to-warmBrown/5 flex flex-col items-center justify-center">
        <img src={NH_LOGO} alt="NH Interiors" className="h-12 w-auto mb-6 opacity-40" />
        <span className="font-heading text-4xl text-warmBrown/30 mb-2">Coming</span>
        <span className="font-heading text-5xl italic text-warmBrown">Soon</span>
        <span className="mt-4 text-xs text-black/40 uppercase tracking-widest font-body">Project in Progress</span>
      </div>
    );
  }

  if (project.image_url === "logo-cover") {
    return (
      <div className="w-full h-full flex items-center justify-center border border-black/5 relative" style={{ background: COVER_BG }}>
        <img src={project.coverLogo} alt={project.title} className="max-h-20 md:max-h-24 w-auto object-contain px-8 transition-opacity duration-500 group-hover:opacity-0" />
        <HoverPreviewImage project={project} />
      </div>
    );
  }

  if (project.image_url === "text-cover") {
    return (
      <div className="w-full h-full flex items-center justify-center border border-black/5 relative" style={{ background: COVER_BG }}>
        <span className="font-heading text-2xl md:text-3xl text-warmBrown italic text-center px-6 transition-opacity duration-500 group-hover:opacity-0">
          {project.coverText}
        </span>
        <HoverPreviewImage project={project} />
      </div>
    );
  }

  if (project.image_url === "nh-cover") {
    return (
      <div className="w-full h-full flex items-center justify-center border border-black/5 relative" style={{ background: COVER_BG }}>
        <img src={NH_LOGO} alt="NH Interiors" className="max-h-16 md:max-h-20 w-auto object-contain transition-opacity duration-500 group-hover:opacity-0" />
        <HoverPreviewImage project={project} />
      </div>
    );
  }

  return (
    <>
      <img
        src={project.image_url}
        alt={project.title}
        className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${project.enhanceColors ? "enhance-colors" : ""}`}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500 flex items-center justify-center">
        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 uppercase tracking-widest text-xs font-body">
          View Project
        </span>
      </div>
    </>
  );
}

function ProjectBadge({ project }) {
  if (project.featured && !project.comingSoon && !project.inProgress) {
    return (
      <span className="absolute top-4 left-4 bg-warmBrown text-white text-xs uppercase tracking-wider px-3 py-1 font-body">
        Featured
      </span>
    );
  }
  if (project.comingSoon || project.inProgress) {
    return (
      <span className="absolute top-4 left-4 bg-primary text-white text-xs uppercase tracking-wider px-3 py-1 font-body">
        In Progress
      </span>
    );
  }
  return null;
}

function ProjectCard({ project, index }) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (project.comingSoon) return;
    navigate(project.dedicatedPage || `/projects/${project.slug}`);
  };

  return (
    <div
      className="group cursor-pointer opacity-0 animate-fade-in"
      style={{ animationDelay: `${index * 100}ms`, animationFillMode: "forwards" }}
      onClick={handleClick}
      data-testid={`project-card-${index}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden mb-6">
        <ProjectCoverImage project={project} />
        <ProjectBadge project={project} />
      </div>
      <div>
        <p className="text-warmBrown text-xs uppercase tracking-widest mb-2 font-body">
          {project.category === "vip" ? "VIP Suite" : project.category}
        </p>
        <h3 className="font-heading text-2xl text-black mb-2 group-hover:text-warmBrown transition-colors duration-300">
          {project.title}
        </h3>
        <p className="text-black/50 font-body text-sm line-clamp-2">
          {project.description}
        </p>
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("filter") || "all";
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [projects] = useState(allProjects);

  const filteredProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  const corporateCount = projects.filter((p) => p.category === "corporate").length;
  const residentialCount = projects.filter((p) => p.category === "residential").length;
  const vipCount = projects.filter((p) => p.category === "vip").length;

  return (
    <div data-testid="portfolio-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end pb-20" data-testid="portfolio-hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Projects"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="container-custom relative z-10 pt-32">
          <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body opacity-0 animate-fade-in">
            Our Work
          </p>
          <h1 className="font-heading text-5xl md:text-7xl text-black opacity-0 animate-fade-in animate-delay-100">
            Spaces That
            <br />
            <span className="italic text-warmBrown">Inspire</span>
          </h1>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-soft-beige border-b border-black/5" data-testid="filter-section">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setActiveFilter("all")}
              className={`px-6 py-3 font-body text-sm uppercase tracking-wider transition-all duration-300 ${
                activeFilter === "all"
                  ? "bg-primary text-white"
                  : "bg-white text-black/60 hover:text-black border border-black/10 hover:border-black/30"
              }`}
              data-testid="filter-all"
            >
              All Projects ({projects.length})
            </button>
            <button
              onClick={() => setActiveFilter("corporate")}
              className={`px-6 py-3 font-body text-sm uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${
                activeFilter === "corporate"
                  ? "bg-primary text-white"
                  : "bg-white text-black/60 hover:text-black border border-black/10 hover:border-black/30"
              }`}
              data-testid="filter-corporate"
            >
              <Building2 size={16} />
              Corporate ({corporateCount})
            </button>
            <button
              onClick={() => setActiveFilter("vip")}
              className={`px-6 py-3 font-body text-sm uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${
                activeFilter === "vip"
                  ? "bg-primary text-white"
                  : "bg-white text-black/60 hover:text-black border border-black/10 hover:border-black/30"
              }`}
              data-testid="filter-vip"
            >
              <Trophy size={16} />
              VIP Boxes ({vipCount})
            </button>
            <button
              onClick={() => setActiveFilter("residential")}
              className={`px-6 py-3 font-body text-sm uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${
                activeFilter === "residential"
                  ? "bg-primary text-white"
                  : "bg-white text-black/60 hover:text-black border border-black/10 hover:border-black/30"
              }`}
              data-testid="filter-residential"
            >
              <Home size={16} />
              Residential ({residentialCount})
            </button>
          </div>
        </div>
      </section>

      {/* Category Header for Residential */}
      {activeFilter === "residential" && (
        <section className="py-12 bg-cream" data-testid="residential-header">
          <div className="container-custom text-center">
            <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
              Premium Collection
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-black mb-4">
              Luxury Residential Projects
            </h2>
            <p className="text-black/60 font-body max-w-2xl mx-auto">
              Our residential portfolio showcases bespoke luxury interiors crafted for 
              discerning homeowners who appreciate exceptional design and meticulous attention to detail.
            </p>
          </div>
        </section>
      )}

      {/* Category Header for VIP Boxes */}
      {activeFilter === "vip" && (
        <section className="py-12 bg-cream" data-testid="vip-header">
          <div className="container-custom text-center">
            <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
              Sport Stadium Hospitality
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-black mb-4">
              VIP Box Suites
            </h2>
            <p className="text-black/60 font-body max-w-2xl mx-auto">
              Premium corporate hospitality suites at Newlands Cricket Stadium, designed to deliver 
              exceptional match day experiences with sophisticated interiors and panoramic views.
            </p>
          </div>
        </section>
      )}

      {/* Projects Grid */}
      <section className="section-padding bg-cream" data-testid="projects-grid">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-soft-beige" data-testid="portfolio-cta-section">
        <div className="container-custom text-center">
          <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
            Ready to Create Your Space?
          </p>
          <h2 className="font-heading text-3xl md:text-5xl text-black mb-6">
            Your Project Could Be
            <br />
            <span className="italic text-warmBrown">Our Next Showcase</span>
          </h2>
          <p className="text-black/60 font-body text-lg max-w-xl mx-auto mb-8">
            Let's discuss how we can transform your vision into a stunning reality
            that reflects your unique style and needs.
          </p>
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-3"
            data-testid="portfolio-cta-button"
          >
            Start Your Project
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
